import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

// Double-click (or the tiny pencil) to rename in place. Enter saves, Esc cancels, blur saves.
export const InlineName = ({ value, onSave, className = '', inputClassName = '', testId, title = 'Double-click to rename' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef(null);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => { if (editing) { ref.current?.focus(); ref.current?.select(); } }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== value) await onSave(next);
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        onBlur={commit}
        className={`min-w-0 w-full bg-white border border-amber-400 rounded px-1 outline-none ring-2 ring-amber-200 ${inputClassName}`}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }

  return (
    <span
      className={`group/name inline-flex items-center gap-1 min-w-0 ${className}`}
      onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }}
      onPointerDown={(e) => { if (e.detail > 1) e.stopPropagation(); }}
      title={title}
      data-testid={testId}
    >
      <span className="truncate">{value}</span>
      <button
        type="button"
        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="opacity-0 group-hover/name:opacity-100 transition-opacity p-0.5 rounded hover:bg-amber-100 flex-shrink-0"
        aria-label="Rename"
        data-testid={testId ? `${testId}-edit` : undefined}
      >
        <Pencil className="w-2.5 h-2.5 text-amber-600" />
      </button>
    </span>
  );
};

export default InlineName;
